import React from "react";
import {
  Box,
  Typography,
  TextField,
  Stack,
  Alert,
  Chip,
  InputBase,
  Skeleton,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import CheckIcon from "@mui/icons-material/Check";
import SearchIcon from "@mui/icons-material/Search";
import SearchOffIcon from "@mui/icons-material/SearchOff";
import { alpha, useTheme } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { components } from "../../types/api";

/** Keeps the list from collapsing while loading / when empty. */
const LIST_MIN_HEIGHT = 220;

type BriefingPlatform = components["schemas"]["BriefingPlatform"];
type PlatformCandidateData = components["schemas"]["PlatformCandidateResponse"];
type EntityType = components["schemas"]["EntityType"];

interface CandidateSelectorProps {
  selectedPlatform: BriefingPlatform;
  /** Drives whether to read insertionOrders or campaigns from the API response */
  defaultCampaignLevel: "CAMPAIGN" | "INSERTIONORDER";
  sourceId: string;
  setSourceId: (id: string) => void;
  /**
   * Optional. When provided, the selector reports the level of the chosen entity
   * (CAMPAIGN vs INSERTIONORDER) so platforms like Spotify can pick either a
   * campaign or an ad set within the same list.
   */
  setCampaignLevel?: (level: EntityType) => void;
  /** Optional. Reports the advertiserId of the chosen entity. */
  setAdvertiserId?: (id: string) => void;
  nativeAdProvider?: "TABOOLA" | "OUTBRAIN" | null;
  setNativeAdProvider?: (provider: "TABOOLA" | "OUTBRAIN" | null) => void;
  canFetchCampaigns: boolean;
  isBusy: boolean;
  candidatesError: unknown;
  candidatesData: PlatformCandidateData | null | undefined;
  handleSave?: () => void;
  hideManualEntry?: boolean;
  /** Hides the "Source ID" title (when the container already shows it). */
  hideLabel?: boolean;
}

export const CandidateSelector: React.FC<CandidateSelectorProps> = ({
  selectedPlatform,
  defaultCampaignLevel,
  sourceId,
  setSourceId,
  setCampaignLevel,
  setAdvertiserId,
  nativeAdProvider,
  setNativeAdProvider,
  canFetchCampaigns,
  isBusy,
  candidatesError,
  candidatesData,
  handleSave,
  hideManualEntry = false,
  hideLabel = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const [sourceIdTab, setSourceIdTab] = React.useState<0 | 1>(0);
  const [filter, setFilter] = React.useState("");

  const campaigns = React.useMemo(
    () => candidatesData?.campaigns ?? [],
    [candidatesData]
  );
  const adSets = React.useMemo(
    () => candidatesData?.insertionOrders ?? [],
    [candidatesData]
  );

  /**
   * Grouped "one or the other" mode: show campaigns with their ad sets nested
   * underneath, letting the user pick either. Enabled generically whenever the
   * platform defaults to CAMPAIGN level but the response also carries ad sets
   * (e.g. Spotify). DV360 keeps its flat insertion-order list (level === INSERTIONORDER).
   */
  const isGroupedMode =
    defaultCampaignLevel === "CAMPAIGN" && adSets.length > 0;

  const allCandidates = React.useMemo(() => {
    if (!candidatesData) return [];

    // Use the server-provided level to decide which entity list to show
    if (defaultCampaignLevel === "INSERTIONORDER") {
      return (candidatesData.insertionOrders ?? []).map((io) => ({
        id: io.id,
        name: io.name,
        originalPlatform: io.platform,
      }));
    }

    // Otherwise, show campaigns
    return (candidatesData.campaigns ?? []).map((c) => ({
      id: c.id,
      name: c.name,
      originalPlatform: c.originalPlatform,
    }));
  }, [candidatesData, defaultCampaignLevel]);

  const filteredCampaigns = React.useMemo(() => {
    let campaignsToFilter = allCandidates;
    if (selectedPlatform === "NATADS" && nativeAdProvider) {
      campaignsToFilter = allCandidates.filter(
        (c) => c.originalPlatform === nativeAdProvider
      );
    }

    let result = campaignsToFilter;
    if (filter.trim()) {
      const q = filter.toLowerCase();
      result = campaignsToFilter.filter(
        (c) =>
          c.name?.toLowerCase().includes(q) ||
          (c.id ?? "").toLowerCase().includes(q)
      );
    }

    // Always sort the selected campaign to the top
    return result.sort((a, b) => {
      if (a.id === sourceId) return -1;
      if (b.id === sourceId) return 1;
      return 0;
    });
  }, [allCandidates, filter, nativeAdProvider, selectedPlatform, sourceId]);

  // Grouped candidates: each campaign with its nested ad sets, filtered by search.
  const groupedCandidates = React.useMemo(() => {
    if (!isGroupedMode)
      return {
        groups: [] as Array<{
          campaign: components["schemas"]["PlatformCandidateCampaign"];
          adSets: components["schemas"]["PlatformCandidateInsertionOrder"][];
          visibleAdSets: components["schemas"]["PlatformCandidateInsertionOrder"][];
          campaignMatches: boolean;
        }>,
        orphanAdSets:
          [] as components["schemas"]["PlatformCandidateInsertionOrder"][],
      };
    const q = filter.trim().toLowerCase();
    const matches = (name?: string, id?: string) =>
      !q ||
      (name ?? "").toLowerCase().includes(q) ||
      (id ?? "").toLowerCase().includes(q);

    const groups = campaigns.map((c) => {
      const childAdSets = adSets.filter((a) => a.campaignId === c.id);
      const campaignMatches = matches(c.name, c.id);
      const visibleAdSets = campaignMatches
        ? childAdSets
        : childAdSets.filter((a) => matches(a.name, a.id));
      return {
        campaign: c,
        adSets: childAdSets,
        visibleAdSets,
        campaignMatches,
      };
    });

    const knownCampaignIds = new Set(campaigns.map((c) => c.id));
    const orphanAdSets = adSets.filter(
      (a) => !knownCampaignIds.has(a.campaignId) && matches(a.name, a.id)
    );

    return { groups, orphanAdSets };
  }, [isGroupedMode, campaigns, adSets, filter]);

  /** Select a campaign: level CAMPAIGN, sourceId = campaign id. */
  const selectCampaign = (
    candidate: components["schemas"]["PlatformCandidateCampaign"]
  ) => {
    if (candidate.id === sourceId) {
      setSourceId("");
      setCampaignLevel?.(defaultCampaignLevel);
      setAdvertiserId?.("");
      return;
    }
    setSourceId(candidate.id ?? "");
    setCampaignLevel?.("CAMPAIGN");
    setAdvertiserId?.(candidate.advertiserId ?? "");
  };

  /** Select an ad set: level INSERTIONORDER, sourceId = ad set id. */
  const selectAdSet = (
    adSet: components["schemas"]["PlatformCandidateInsertionOrder"]
  ) => {
    if (adSet.id === sourceId) {
      setSourceId("");
      setCampaignLevel?.(defaultCampaignLevel);
      setAdvertiserId?.("");
      return;
    }
    setSourceId(adSet.id ?? "");
    setCampaignLevel?.("INSERTIONORDER");
    setAdvertiserId?.(adSet.advertiserId ?? "");
  };

  const clearSelection = () => {
    setSourceId("");
    setCampaignLevel?.(defaultCampaignLevel);
    setAdvertiserId?.("");
  };

  /** Flat list (no ad sets): NATADS also reports the native provider. */
  const toggleFlatCandidate = (candidate: (typeof allCandidates)[number]) => {
    const reportsProvider =
      selectedPlatform === "NATADS" && setNativeAdProvider;
    if (candidate.id === sourceId) {
      setSourceId("");
      if (reportsProvider) setNativeAdProvider(null);
      return;
    }
    setSourceId(candidate.id ?? "");
    if (reportsProvider && candidate.originalPlatform) {
      setNativeAdProvider(candidate.originalPlatform as "TABOOLA" | "OUTBRAIN");
    }
  };

  const renderSelectableRow = (
    id: string | undefined,
    name: string | undefined,
    onClick: () => void,
    opts?: { nested?: boolean; chipLabel?: string }
  ) => {
    const isSelected = id === sourceId && !!id;
    return (
      <Box
        key={id}
        role="option"
        aria-selected={isSelected}
        tabIndex={0}
        onClick={onClick}
        onKeyDown={(event) => {
          if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            event.stopPropagation();
            onClick();
          }
        }}
        sx={{
          pl: opts?.nested ? 4.5 : 2,
          pr: 1.5,
          py: 1.5,
          cursor: "pointer",
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          borderBottom: `1px solid ${theme.palette.divider}`,
          "&:last-of-type": { borderBottom: "none" },
          bgcolor: isSelected
            ? alpha(theme.palette.text.primary, 0.06)
            : "transparent",
          transition: "background-color .15s",
          "&:hover, &:focus-visible": {
            bgcolor: isSelected
              ? alpha(theme.palette.text.primary, 0.09)
              : theme.palette.action.hover,
            outline: "none",
          },
        }}
      >
        <Box minWidth={0} flex={1}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography
              variant="body2"
              fontWeight={isSelected ? 700 : 500}
              sx={{ wordBreak: "break-word" }}
            >
              {name}
            </Typography>
            {opts?.chipLabel && (
              <Chip
                size="small"
                label={opts.chipLabel}
                variant="outlined"
                sx={{ height: 18, fontSize: 10, flexShrink: 0 }}
              />
            )}
          </Stack>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ fontFamily: "monospace", wordBreak: "break-all" }}
          >
            {id}
          </Typography>
        </Box>
        <Box
          sx={{
            width: 22,
            height: 22,
            borderRadius: "50%",
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `1.5px solid ${
              isSelected ? theme.palette.text.primary : theme.palette.divider
            }`,
            bgcolor: isSelected ? theme.palette.text.primary : "transparent",
            color: theme.palette.background.paper,
          }}
        >
          {isSelected && <CheckIcon sx={{ fontSize: 15 }} />}
        </Box>
      </Box>
    );
  };

  const renderMessage = (icon: React.ReactNode, text: string) => (
    <Stack
      alignItems="center"
      justifyContent="center"
      spacing={1}
      sx={{ minHeight: LIST_MIN_HEIGHT, px: 2, textAlign: "center" }}
    >
      <Box sx={{ color: "text.disabled", display: "flex" }}>{icon}</Box>
      <Typography variant="body2" color="text.secondary">
        {text}
      </Typography>
    </Stack>
  );

  const visibleGroups = groupedCandidates.groups.filter(
    (g) => g.campaignMatches || g.visibleAdSets.length > 0
  );
  const resultCount = isGroupedMode
    ? visibleGroups.reduce((n, g) => n + 1 + g.visibleAdSets.length, 0) +
      groupedCandidates.orphanAdSets.length
    : filteredCampaigns.length;

  const renderList = () => {
    if (isBusy) {
      return (
        <Box sx={{ minHeight: LIST_MIN_HEIGHT }}>
          {[0, 1, 2, 3].map((i) => (
            <Box
              key={i}
              sx={{
                px: 2,
                py: 1.5,
                borderBottom:
                  i < 3 ? `1px solid ${theme.palette.divider}` : "none",
              }}
            >
              <Skeleton variant="text" width={`${70 - i * 10}%`} height={20} />
              <Skeleton variant="text" width="35%" height={16} />
            </Box>
          ))}
        </Box>
      );
    }
    if (candidatesError) {
      return (
        <Box sx={{ p: 1.5, minHeight: LIST_MIN_HEIGHT }}>
          <Alert severity="error">
            {t(
              "briefingsView.campaignFetchError",
              "Failed to load campaigns. Use manual entry."
            )}
          </Alert>
        </Box>
      );
    }
    if (resultCount === 0) {
      return renderMessage(
        <SearchOffIcon />,
        t("briefingsView.noCampaignsFound", "No campaigns found")
      );
    }
    if (!isGroupedMode) {
      return filteredCampaigns.map((campaign) =>
        renderSelectableRow(campaign.id, campaign.name, () =>
          toggleFlatCandidate(campaign)
        )
      );
    }
    return (
      <>
        {visibleGroups.map((group) => (
          <React.Fragment key={group.campaign.id}>
            {renderSelectableRow(
              group.campaign.id,
              group.campaign.name,
              () => selectCampaign(group.campaign),
              { chipLabel: t("briefingsView.campaign", "Campaign") }
            )}
            {group.visibleAdSets.map((adSet) =>
              renderSelectableRow(
                adSet.id,
                adSet.name,
                () => selectAdSet(adSet),
                { nested: true, chipLabel: t("briefingsView.adSet", "Ad set") }
              )
            )}
          </React.Fragment>
        ))}
        {groupedCandidates.orphanAdSets.length > 0 && (
          <>
            <Typography
              variant="overline"
              color="text.secondary"
              sx={{
                display: "block",
                px: 2,
                pt: 1.5,
                pb: 0.5,
                fontWeight: 700,
                bgcolor: theme.palette.action.hover,
              }}
            >
              {t("briefingsView.otherAdSets", "Other ad sets")}
            </Typography>
            {groupedCandidates.orphanAdSets.map((adSet) =>
              renderSelectableRow(
                adSet.id,
                adSet.name,
                () => selectAdSet(adSet),
                { nested: true, chipLabel: t("briefingsView.adSet", "Ad set") }
              )
            )}
          </>
        )}
      </>
    );
  };

  const manualInput = (
    <TextField
      size="small"
      label={t("briefingsView.sourceId", "Source ID")}
      value={sourceId}
      onChange={(e) => setSourceId(e.target.value)}
      fullWidth
      placeholder={t(
        "briefingsView.sourceIdPlaceholder",
        "Optional — can be added later"
      )}
      onKeyDown={(e) => {
        if (e.key === "Enter" && handleSave) {
          e.preventDefault();
          handleSave();
        }
      }}
      sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
    />
  );

  return (
    <Box>
      {!hideLabel && (
        <Typography variant="subtitle2" color="text.secondary" mb={1}>
          {t("briefingsView.sourceId", "Source ID")}
        </Typography>
      )}

      {!canFetchCampaigns ? (
        manualInput
      ) : (
        <Stack spacing={1.5}>
          {!hideManualEntry && (
            <ToggleButtonGroup
              exclusive
              size="small"
              value={sourceIdTab}
              onChange={(_, v) => v !== null && setSourceIdTab(v as 0 | 1)}
              sx={{
                alignSelf: "flex-start",
                bgcolor: theme.palette.action.hover,
                borderRadius: 2,
                p: 0.4,
                gap: 0.4,
                "& .MuiToggleButton-root": {
                  border: "none",
                  borderRadius: "6px !important",
                  px: 1.5,
                  py: 0.5,
                  textTransform: "none",
                  fontWeight: 600,
                  color: "text.secondary",
                  "&.Mui-selected": {
                    bgcolor: "background.paper",
                    color: "text.primary",
                    boxShadow: theme.shadows[1],
                    "&:hover": { bgcolor: "background.paper" },
                  },
                },
              }}
            >
              <ToggleButton value={0}>
                {t("briefingsView.fromPlatform", "Browse {{platform}}", {
                  platform: selectedPlatform,
                })}
              </ToggleButton>
              <ToggleButton value={1}>
                {t("briefingsView.manualEntry", "Manual entry")}
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          {sourceIdTab === 1 && !hideManualEntry ? (
            manualInput
          ) : (
            <Box
              sx={{
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "background.paper",
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                sx={{
                  px: 1.5,
                  py: 1,
                  borderBottom: `1px solid ${theme.palette.divider}`,
                  bgcolor: theme.palette.action.hover,
                }}
              >
                <SearchIcon sx={{ fontSize: 18, color: "text.secondary" }} />
                <InputBase
                  value={filter}
                  onChange={(e) => setFilter(e.target.value)}
                  placeholder={t("common.search", "Search by name or ID...")}
                  sx={{ flex: 1, fontSize: 14 }}
                  inputProps={{ "aria-label": t("common.search", "Search") }}
                />
                {!isBusy && !candidatesError && (
                  <Typography variant="caption" color="text.secondary" noWrap>
                    {t("briefingsView.candidateCount", {
                      count: resultCount,
                      defaultValue: "{{count}} results",
                    })}
                  </Typography>
                )}
              </Stack>
              <Box role="listbox" sx={{ maxHeight: 340, overflowY: "auto" }}>
                {renderList()}
              </Box>
              {sourceId && (
                <Stack
                  direction="row"
                  alignItems="center"
                  spacing={1}
                  sx={{
                    px: 1.5,
                    py: 1,
                    borderTop: `1px solid ${theme.palette.divider}`,
                    bgcolor: theme.palette.action.hover,
                  }}
                >
                  <Typography variant="caption" color="text.secondary">
                    {t("common.selected", "Selected")}:
                  </Typography>
                  <Chip
                    size="small"
                    label={sourceId}
                    onDelete={clearSelection}
                    sx={{
                      fontFamily: "monospace",
                      bgcolor: "background.paper",
                    }}
                  />
                </Stack>
              )}
            </Box>
          )}
        </Stack>
      )}
    </Box>
  );
};
